import { TRIAL_DURATION_DAYS } from '../constants/index.js';
import type { UserRole } from './user.js';

/**
 * Trial arithmetic (§11).
 *
 * Pure functions with an injectable `now` so both the server and the tests
 * reason about the same rules. The trial begins on the first successful login,
 * not at registration.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export type TrialState = {
  /**
   * Пробный период к этому человеку не применяется.
   *
   * Он для тех, кто ещё ничего не приобрёл. Администратору и тому, кому доступ
   * выдан вручную, обратный отсчёт ничего не измеряет: их доступ определяется
   * ролью и выданными правами, и его окончание у них ничего не закрывает.
   */
  exempt: boolean;
  /** The user has logged in at least once, so the clock has been started. */
  started: boolean;
  active: boolean;
  startedAt: string | null;
  endsAt: string | null;
  /** Whole days remaining, rounded up; 0 once the trial has ended. */
  daysLeft: number | null;
  expired: boolean;
};

export type TrialWindow = { startedAt: string; endsAt: string };

/**
 * Кому пробный период не применяется (§11).
 *
 * Администратор — по роли, без отдельной отметки: доступ администратора не
 * зависит от пробного периода, и отсчёт у него измерял бы пустоту. Остальным
 * отметку ставит администратор вручную — тому, кто продукт купил.
 */
export function isTrialExempt(user: { role: UserRole; trialExempt: boolean }): boolean {
  return user.role === 'admin' || user.trialExempt;
}

/** Produces the window stored on the user at first login. */
export function startTrial(now: Date, durationDays = TRIAL_DURATION_DAYS): TrialWindow {
  const endsAt = new Date(now.getTime() + durationDays * MS_PER_DAY);
  return { startedAt: now.toISOString(), endsAt: endsAt.toISOString() };
}

export function computeTrialState(
  startedAt: string | null,
  endsAt: string | null,
  now: Date = new Date(),
  options: { exempt?: boolean } = {},
): TrialState {
  const started = Boolean(startedAt && endsAt);

  /*
    Отметка сильнее дат. Если отсчёт когда-то начался, а потом человек купил
    продукт, даты остаются как след — но период у него не идёт и не кончается.
    Снимут отметку — вернётся то, что было, а не начнётся заново.
  */
  if (options.exempt) {
    return {
      exempt: true,
      started,
      active: false,
      startedAt,
      endsAt,
      daysLeft: null,
      expired: false,
    };
  }

  if (!startedAt || !endsAt) {
    return {
      exempt: false,
      started: false,
      active: false,
      startedAt,
      endsAt,
      daysLeft: null,
      expired: false,
    };
  }

  const end = new Date(endsAt).getTime();
  const remaining = end - now.getTime();
  const active = remaining > 0;

  return {
    exempt: false,
    started: true,
    active,
    startedAt,
    endsAt,
    daysLeft: active ? Math.ceil(remaining / MS_PER_DAY) : 0,
    expired: !active,
  };
}

/** «Пробный период: осталось 11 дней» (§11). */
export function formatDaysLeft(daysLeft: number): string {
  const lastTwo = daysLeft % 100;
  const last = daysLeft % 10;

  const word =
    lastTwo >= 11 && lastTwo <= 14
      ? 'дней'
      : last === 1
        ? 'день'
        : last >= 2 && last <= 4
          ? 'дня'
          : 'дней';

  return `${daysLeft} ${word}`;
}
